import { useCallback, useEffect, useRef, useState } from "react";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { Dropdown } from "primereact/dropdown";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { getAdminSettings } from "sj-ui-toolkit/src/api/admin/common";
import { setAdminSettingValue } from "sj-ui-toolkit/src/api/admin/settings";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface XapiSettings {
  xapiLrsUrl?: string;
  xapiUsername?: string;
  xapiPassword?: string;
  xapiOauthKey?: string;
  xapiOauthSecret?: string;
  xapiAuthenticationType?: string;
  xapiAuthenticationTypes?: string[];
  xapiOauthTokenUrl?: string;
  xapiOauthScope?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const XapiConnection = () => {
  useBreadcrumb({
    label: "xAPI / LRS",
    route: AdminAppRoutes.routes.connections.routes.xapi.route,
    display: "page",
  });

  const toast = useRef<Toast>(null);

  const [settings, setSettings] = useState<XapiSettings>({});
  const [localValues, setLocalValues] = useState<XapiSettings>({});
  const [locked, setLocked] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const showLocalToast = useCallback(
    (
      severity: "success" | "info" | "warn" | "error",
      summary: string,
      detail: string,
      life = 3000
    ) => {
      toast.current?.show({ severity, summary, detail, life });
    },
    []
  );

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getAdminSettings();
        const loaded: XapiSettings = {
          xapiLrsUrl: data?.xapiLrsUrl,
          xapiUsername: data?.xapiUsername,
          xapiOauthKey: data?.xapiOauthKey,
          xapiAuthenticationType: data?.xapiAuthenticationType || "basic",
          xapiAuthenticationTypes: data?.xapiAuthenticationTypes,
          xapiOauthTokenUrl:
            typeof data?.xapiOauthTokenUrl === "string" ? data.xapiOauthTokenUrl : undefined,
          xapiOauthScope:
            typeof data?.xapiOauthScope === "string" ? data.xapiOauthScope : undefined,
        };
        setSettings(loaded);
        setLocalValues({
          ...loaded,
          xapiPassword: "",
          xapiOauthSecret: "",
        });
      } catch (error) {
        console.error("Failed to load xAPI settings", error);
        showLocalToast("error", "Error", "Failed to load xAPI settings", 5000);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [showLocalToast]);

  const authType = localValues.xapiAuthenticationType || "basic";

  const handleUnlockClick = () => {
    confirmDialog({
      message: (
        <div className="space-y-2">
          <div className="font-semibold text-lg">Unlock LRS Settings?</div>
          <div className="body-sm">
            These settings control how learning data is sent to your Learning Record Store. Changing
            these values incorrectly may disrupt xAPI data transmission.
          </div>
        </div>
      ),
      header: "Confirm Unlock",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => setLocked(false),
    });
  };

  const handleFieldChange = (fieldName: keyof XapiSettings, value: string) => {
    setLocalValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleFieldBlur = async (fieldName: keyof XapiSettings) => {
    const newValue = localValues[fieldName];
    const isPasswordField = fieldName === "xapiPassword" || fieldName === "xapiOauthSecret";

    // Never save an empty password field — blank means "no change"
    if (isPasswordField && !newValue) return;

    const oldValue = settings[fieldName];

    if (newValue !== oldValue) {
      try {
        const result = await setAdminSettingValue(fieldName, String(newValue || ""));
        if (!result) throw new Error("Failed to update setting");
        setSettings(prev => ({ ...prev, [fieldName]: newValue }));
        if (isPasswordField) {
          setLocalValues(prev => ({ ...prev, [fieldName]: "" }));
        }
        showLocalToast("success", "Success", "Setting updated successfully");
      } catch (error) {
        console.error(`Failed to update ${fieldName}`, error);
        showLocalToast("error", "Error", "Failed to update setting. Please try again.");
      }
    }
  };

  const handleAuthTypeChange = async (e: { value: string }) => {
    const newValue = e.value;
    setLocalValues(prev => ({ ...prev, xapiAuthenticationType: newValue }));

    try {
      const result = await setAdminSettingValue("xapiAuthenticationType", newValue);
      if (!result) throw new Error("Failed to update authentication type");
      setSettings(prev => ({ ...prev, xapiAuthenticationType: newValue }));
      showLocalToast("success", "Success", "Authentication type updated successfully");
    } catch (error) {
      console.error("Failed to update authentication type", error);
      showLocalToast("error", "Error", "Failed to update authentication type. Please try again.");
    }
  };

  const basicFields = [
    { name: "xapiLrsUrl" as const, label: "LRS URL", type: "text" },
    { name: "xapiAuthenticationType" as const, label: "Authentication Type", type: "select" },
    { name: "xapiUsername" as const, label: "Username", type: "text" },
    { name: "xapiPassword" as const, label: "Password", type: "password" },
  ];

  const oauthFields = [
    { name: "xapiLrsUrl" as const, label: "LRS URL", type: "text" },
    { name: "xapiOauthTokenUrl" as const, label: "Token URL", type: "text" },
    { name: "xapiOauthKey" as const, label: "Key", type: "text" },
    { name: "xapiOauthSecret" as const, label: "Secret", type: "password" },
    { name: "xapiOauthScope" as const, label: "Scope (Optional)", type: "text" },
    { name: "xapiAuthenticationType" as const, label: "Authentication Type", type: "select" },
  ];

  const fields = authType === "oauth" ? oauthFields : basicFields;

  const renderField = (field: { name: keyof XapiSettings; label: string; type: string }) => {
    const value = (localValues[field.name] as string) ?? "";

    if (field.type === "password") {
      const placeholder = `Enter ${field.label.toLowerCase()} (leave blank to keep unchanged)`;
      return (
        <Password
          id={field.name}
          inputId={field.name}
          value={value}
          onChange={e => handleFieldChange(field.name, e.target.value)}
          onBlur={() => handleFieldBlur(field.name)}
          disabled={locked || isLoading}
          feedback={false}
          placeholder={placeholder}
          style={{ width: "100%" }}
          inputStyle={{ width: "100%" }}
          className="w-full [&_.p-input-icon-right]:!w-full"
          aria-required={!field.label.includes("Optional")}
        />
      );
    }

    if (field.type === "select") {
      const options = settings.xapiAuthenticationTypes?.map(type => ({
        label: type.charAt(0).toUpperCase() + type.slice(1),
        value: type,
      })) || [
        { label: "Basic", value: "basic" },
        { label: "OAuth", value: "oauth" },
      ];

      return (
        <Dropdown
          id={field.name}
          inputId={field.name}
          value={localValues.xapiAuthenticationType || "basic"}
          options={options}
          onChange={handleAuthTypeChange}
          disabled={locked || isLoading}
          className="w-full"
        />
      );
    }

    return (
      <InputText
        id={field.name}
        value={value}
        onChange={e => handleFieldChange(field.name, e.target.value)}
        onBlur={() => handleFieldBlur(field.name)}
        disabled={locked || isLoading}
        className="w-full"
        aria-required={!field.label.includes("Optional")}
      />
    );
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="mb-4">
        <h1>xAPI / Learning Record Store</h1>
      </div>
      <Card>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <h2 className="atlas-h4">xAPI Configuration</h2>
            {!isLoading && (
              <Tag
                value={settings.xapiLrsUrl ? "Configured" : "Not configured"}
                severity={settings.xapiLrsUrl ? "success" : "secondary"}
              />
            )}
          </div>
          {isLoading ? (
            <ProgressSpinner style={{ width: "30px", height: "30px" }} />
          ) : (
            <Button
              outlined
              icon={locked ? "pi pi-lock" : "pi pi-lock-open"}
              label={locked ? "Locked" : "Unlocked"}
              onClick={locked ? handleUnlockClick : () => setLocked(true)}
              type="button"
              aria-label={locked ? "Unlock LRS settings" : "Lock LRS settings"}
            />
          )}
        </div>

        <p className="body-sm text-secondary mb-4">
          Configure your Learning Record Store (LRS) to receive xAPI statements for learner activity
          tracking.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {fields.map(field => (
            <div key={field.name}>
              <label htmlFor={field.name} className="block mb-1 font-medium">
                {field.label}
              </label>
              {renderField(field)}
            </div>
          ))}
        </div>
      </Card>
    </>
  );
};

export default XapiConnection;
